import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const CARD = {
  laborable: "border-amber-300/80 bg-gradient-to-br from-amber-50 to-yellow-50 text-amber-950",
  vacaciones: "border-violet-400 bg-violet-200/90 text-violet-950",
  ausencia: "border-orange-300 bg-orange-200 text-orange-950",
  permiso_justificado: "border-orange-300/90 bg-gradient-to-br from-orange-100 to-amber-50 text-orange-950",
  baja: "border-red-300 bg-red-200 text-red-950",
  descanso: "border-zinc-200 bg-zinc-100/80 text-zinc-500",
  festivo: "border-sky-300 bg-sky-100 text-sky-950",
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function lunesDe(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function esHoy(fecha) {
  return fecha === hoyISO();
}

function CeldaDia({ dia, compacto = false }) {
  const cls = CARD[dia.codigo] || CARD.laborable;
  const style =
    dia.color && (dia.codigo === "laborable" || dia.codigo === "permiso_justificado")
      ? { borderColor: dia.color, backgroundColor: `${dia.color}22` }
      : undefined;

  return (
    <div
      data-testid={`cal-dia-${dia.fecha}`}
      style={style}
      className={`relative rounded-xl border px-3 py-2.5 ${cls} ${esHoy(dia.fecha) ? "ring-2 ring-indigo-400 ring-offset-1" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-wide opacity-70">
            {compacto ? dia.dia_semana_corto : dia.dia_semana}
            {" · "}
            {fmtFecha(dia.fecha)}
          </p>
          <p className="mt-0.5 text-sm font-semibold leading-snug">{dia.label}</p>
          {dia.detalle && dia.codigo !== "festivo" && (
            <p className="mt-0.5 text-xs opacity-80">{dia.detalle}</p>
          )}
          {dia.horas && (
            <p className="mt-1 text-[11px] font-medium opacity-60">{dia.horas} previstas</p>
          )}
          {dia.festivo && dia.codigo !== "festivo" && (
            <p className="mt-1 text-[11px] text-sky-700">Festivo: {dia.festivo.nombre}</p>
          )}
        </div>
        {dia.turno_nombre && dia.codigo === "laborable" && (
          <Badge variant="outline" className="shrink-0 border-amber-300/60 bg-white/60 text-[10px]">
            Turno
          </Badge>
        )}
      </div>
    </div>
  );
}

function VistaSemana({ dias }) {
  return (
    <div className="space-y-2">
      {dias.map((dia) => (
        <CeldaDia key={dia.fecha} dia={dia} />
      ))}
    </div>
  );
}

function VistaMes({ dias, year, month }) {
  const mapa = useMemo(() => Object.fromEntries(dias.map((d) => [d.fecha, d])), [dias]);
  const primerDia = new Date(year, month - 1, 1);
  const ultimoDia = new Date(year, month, 0).getDate();
  const offset = (primerDia.getDay() + 6) % 7;

  const celdas = [];
  for (let i = 0; i < offset; i += 1) {
    celdas.push({ vacio: true, key: `e-${i}` });
  }
  for (let d = 1; d <= ultimoDia; d += 1) {
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    celdas.push({ vacio: false, key: iso, dia: mapa[iso], num: d, iso });
  }

  return (
    <div>
      <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-zinc-400">
        {["L", "M", "X", "J", "V", "S", "D"].map((l) => (
          <span key={l}>{l}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {celdas.map((c) => {
          if (c.vacio) {
            return <div key={c.key} className="aspect-square rounded-lg bg-transparent" />;
          }
          const codigo = c.dia?.codigo || "descanso";
          const cls = CARD[codigo] || CARD.descanso;
          const style =
            c.dia?.color && codigo === "laborable"
              ? { backgroundColor: `${c.dia.color}33`, borderColor: c.dia.color }
              : undefined;
          return (
            <div
              key={c.key}
              title={c.dia ? `${c.dia.label}${c.dia.detalle ? ` · ${c.dia.detalle}` : ""}` : "Sin datos"}
              style={style}
              className={`flex aspect-square flex-col items-center justify-center rounded-lg border text-center ${cls} ${
                esHoy(c.iso) ? "ring-2 ring-indigo-400" : ""
              }`}
            >
              <span className="text-[11px] font-bold">{c.num}</span>
              {c.dia?.turno_nombre && (
                <span className="mt-0.5 line-clamp-2 px-0.5 text-[7px] leading-tight opacity-80">
                  {c.dia.turno_nombre}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 space-y-2 md:hidden">
        {dias
          .filter((d) => d.codigo === "laborable" || d.codigo === "permiso_justificado")
          .map((dia) => (
            <CeldaDia key={dia.fecha} dia={dia} compacto />
          ))}
      </div>
    </div>
  );
}

function Leyenda({ items }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(items || []).map((item) => (
        <span
          key={item.codigo}
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${CARD[item.codigo] || CARD.descanso}`}
        >
          {item.label}
        </span>
      ))}
    </div>
  );
}

export default function MiCalendario() {
  const hoy = new Date();
  const [vista, setVista] = useState("semana");
  const [semanaInicio, setSemanaInicio] = useState(() => lunesDe(hoyISO()));
  const [year, setYear] = useState(hoy.getFullYear());
  const [month, setMonth] = useState(hoy.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      let params;
      if (vista === "mes") {
        params = { year, month };
      } else {
        const fin = new Date(`${semanaInicio}T12:00:00`);
        fin.setDate(fin.getDate() + 6);
        params = { desde: semanaInicio, hasta: fin.toISOString().slice(0, 10) };
      }
      const { data: res } = await api.get("/trabajador/mi-calendario", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [vista, semanaInicio, year, month]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const navegarSemana = (delta) => {
    const d = new Date(`${semanaInicio}T12:00:00`);
    d.setDate(d.getDate() + delta * 7);
    setSemanaInicio(d.toISOString().slice(0, 10));
  };

  const navegarMes = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const tituloPeriodo =
    vista === "mes"
      ? `${MESES[month - 1]} ${year}`
      : data?.desde && data?.hasta
        ? `${fmtFecha(data.desde)} – ${fmtFecha(data.hasta)}`
        : "Semana actual";

  return (
    <div className="space-y-4" data-testid="mi-calendario-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
          Mi calendario
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Turnos, jornadas y días libres según tu horario
        </p>
      </div>

      {data && (
        <div className="rounded-2xl border border-indigo-100/90 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-800">
              {data.modo_horario_label}
            </Badge>
            <span className="text-zinc-500">{data.horario_origen_label}</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-amber-50 px-2 py-2 dark:bg-amber-950/30">
              <p className="text-lg font-bold text-amber-900 dark:text-amber-100">{data.dias_laborables}</p>
              <p className="text-[10px] text-amber-700 dark:text-amber-300">Laborables</p>
            </div>
            <div className="rounded-xl bg-zinc-100 px-2 py-2 dark:bg-zinc-800">
              <p className="text-lg font-bold text-zinc-700 dark:text-zinc-200">{data.dias_descanso}</p>
              <p className="text-[10px] text-zinc-500">Descanso</p>
            </div>
            <div className="rounded-xl bg-violet-50 px-2 py-2 dark:bg-violet-950/30">
              <p className="text-lg font-bold text-violet-900 dark:text-violet-100">{data.horas_periodo}</p>
              <p className="text-[10px] text-violet-700 dark:text-violet-300">Horas previstas</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-xl border border-indigo-100 bg-white p-1 dark:border-zinc-800 dark:bg-zinc-900">
          <button
            type="button"
            onClick={() => setVista("semana")}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
              vista === "semana" ? "bg-indigo-600 text-white" : "text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Semana
          </button>
          <button
            type="button"
            onClick={() => setVista("mes")}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
              vista === "mes" ? "bg-indigo-600 text-white" : "text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Mes
          </button>
        </div>

        <div className="flex items-center gap-1 rounded-xl border border-indigo-100 bg-white p-1 dark:border-zinc-800 dark:bg-zinc-900">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => (vista === "mes" ? navegarMes(-1) : navegarSemana(-1))}
            aria-label="Periodo anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex min-w-[8rem] items-center justify-center gap-1 px-1 text-xs font-semibold text-zinc-800 dark:text-zinc-200">
            <CalendarDays className="h-3.5 w-3.5 text-indigo-500" />
            {tituloPeriodo}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => (vista === "mes" ? navegarMes(1) : navegarSemana(1))}
            aria-label="Periodo siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-indigo-100/90 bg-white px-5 py-12 text-center text-sm text-zinc-400 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          Cargando calendario…
        </div>
      ) : data?.dias?.length ? (
        <>
          {vista === "semana" ? (
            <VistaSemana dias={data.dias} />
          ) : (
            <VistaMes dias={data.dias} year={year} month={month} />
          )}
          <Leyenda items={data.leyenda} />
        </>
      ) : (
        <div className="rounded-2xl border border-indigo-100/90 bg-white px-5 py-12 text-center text-sm text-zinc-500 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          No hay datos de calendario para este periodo.
        </div>
      )}
    </div>
  );
}
